import { Component, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { EventMap } from "../map/EventMap";
import type {
  MapPin,
  MapRoute,
  MapRouteStyle,
  PublishedMap,
} from "../shared/types";
import type {
  EmbedHandle,
  EmbedOptions,
  ErrorDetail,
  ReadyDetail,
  SelectDetail,
  ViewChangeDetail,
  RouteChangeDetail,
} from "./types";
import {
  EmbedError,
  mapURL,
  readPublishedMap,
  validateLocations,
  validatePins,
  validateRoute,
  validateRouteStyle,
  validateControlNumber,
} from "./validation";
import styles from "./embed.css?inline";
import type {
  EventMapHandle,
  MapRouteChange,
  MapViewState,
} from "../shared/mapControls";
import { createMapDiagnostics } from "./diagnostics";
import { isMapLocationHidden } from "../shared/mapPresentation";

class MapBoundary extends Component<
  { children: ReactNode; onError: (error: Error) => void },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error) {
    this.props.onError(error);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function deferred() {
  let resolve!: () => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<void>((yes, no) => {
    resolve = yes;
    reject = no;
  });
  // Automatic script embeds use events; an unobserved ready promise must not become an uncaught rejection.
  void promise.catch(() => {});
  return { promise, resolve, reject };
}

export async function mountMap(
  target: HTMLElement | string,
  options: EmbedOptions,
  defaultBase: string,
): Promise<EmbedHandle> {
  const destination =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!(destination instanceof HTMLElement))
    throw new EmbedError(
      "invalid_target",
      "The map target must be an HTML element or a selector matching one.",
    );
  if (!options || typeof options !== "object")
    throw new EmbedError("invalid_options", "Map options are required.");
  const url = mapURL(options.mapId, options.apiBase ?? defaultBase);
  if (options.controls !== undefined && typeof options.controls !== "boolean")
    throw new EmbedError(
      "invalid_options",
      "The controls option must be true or false.",
    );
  if (options.onSelect !== undefined && typeof options.onSelect !== "function")
    throw new EmbedError(
      "invalid_options",
      "The onSelect option must be a function.",
    );
  if (
    options.onDiagnostic !== undefined &&
    typeof options.onDiagnostic !== "function"
  )
    throw new EmbedError(
      "invalid_options",
      "The onDiagnostic option must be a function.",
    );
  if (options.nonce !== undefined && typeof options.nonce !== "string")
    throw new EmbedError(
      "invalid_options",
      "The nonce option must be a string.",
    );
  let pins = validatePins(options.pins ?? []);
  let route = validateRoute(options.route);
  let routeStyle = validateRouteStyle(
    options.routeStyle === undefined ? {} : options.routeStyle,
  );
  const diagnostics = createMapDiagnostics(options.onDiagnostic);
  let snapshot: PublishedMap | undefined;
  let ready = deferred();
  let renderer: EventMapHandle | undefined;
  let lastView: MapViewState | undefined;
  let lastRoute: MapRouteChange | undefined;
  let emittedView = "";
  let emittedRoute = "";
  let requestedFit = false;
  let requestedRouteFit = false;
  let destroyed = false;
  let generation = 0;
  let controller: AbortController | undefined;
  let deadline: ReturnType<typeof setTimeout> | undefined;
  let readyAnnounced = false;

  const host = document.createElement("div");
  host.dataset.eventMap = options.mapId;
  const shadow = host.attachShadow({ mode: "open" });
  const style = document.createElement("style");
  if (options.nonce) style.nonce = options.nonce;
  style.textContent = styles;
  const frame = document.createElement("section");
  frame.className = "event-map-embed";
  frame.setAttribute("aria-label", "Interactive venue map");
  const content = document.createElement("div");
  content.className = "event-map-embed__content";
  frame.appendChild(content);
  shadow.append(style, frame);
  destination.appendChild(host);
  const root = createRoot(content);

  function emit<T>(name: string, detail: T) {
    if (!destroyed)
      host.dispatchEvent(
        new CustomEvent(name, { detail, bubbles: true, composed: true }),
      );
  }
  function stopRequest() {
    clearTimeout(deadline);
    deadline = undefined;
    controller?.abort();
    controller = undefined;
  }
  function alive() {
    if (destroyed)
      throw new EmbedError(
        "destroyed",
        "This map instance has been destroyed.",
      );
  }
  function camera(): EventMapHandle {
    alive();
    if (!renderer || !readyAnnounced)
      throw new EmbedError(
        "not_ready",
        "Wait for this map's ready promise before using camera controls.",
      );
    return renderer;
  }
  function announceView(view: MapViewState) {
    lastView = { ...view };
    const key = JSON.stringify(view);
    if (!readyAnnounced || emittedView === key) return;
    emittedView = key;
    emit<ViewChangeDetail>("event-map:viewchange", {
      mapId: options.mapId,
      view: { ...view },
    });
  }
  function announceRoute(change: MapRouteChange) {
    lastRoute = {
      route: change.route ? { ...change.route } : null,
      status: change.status,
    };
    const key = JSON.stringify(lastRoute);
    if (!readyAnnounced || emittedRoute === key) return;
    emittedRoute = key;
    emit<RouteChangeDetail>("event-map:routechange", {
      mapId: options.mapId,
      route: lastRoute.route ? { ...lastRoute.route } : null,
      status: lastRoute.status,
    });
  }
  function showError(
    cause: unknown,
    token: number,
    phase: "load" | "render" = "load",
  ) {
    if (destroyed || token !== generation) return;
    stopRequest();
    const error =
      cause instanceof EmbedError
        ? cause
        : new EmbedError(
            "load_failed",
            "The map could not load. Please check your connection and try again.",
          );
    diagnostics.record(
      phase === "render"
        ? "render_failed"
        : error.code === "timeout"
          ? "load_timeout"
          : "load_failed",
    );
    ready.reject(error);
    readyAnnounced = false;
    renderer = undefined;
    snapshot = undefined;
    root.render(
      <div className="event-map-embed__status" role="alert">
        <h2>Map unavailable</h2>
        <p>{error.message}</p>
        <button
          type="button"
          onClick={() => {
            void load().catch(() => {});
          }}
        >
          Try again
        </button>
      </div>,
    );
    emit<ErrorDetail>("event-map:error", {
      mapId: options.mapId,
      code: error.code,
      message: error.message,
    });
  }
  function renderMap(token: number) {
    if (!snapshot || destroyed || token !== generation) return;
    const publication = snapshot;
    root.render(
      <MapBoundary
        key={token}
        onError={(error) => showError(error, token, "render")}
      >
        <EventMap
          document={publication.map}
          pins={pins}
          route={route}
          routeStyle={routeStyle}
          controls={options.controls ?? true}
          styleNonce={options.nonce}
          logoBaseUrl={new URL(`${url.pathname}/logos`, url).href}
          onRendererFailure={(reason) => {
            if (!destroyed && token === generation) diagnostics.record(reason);
          }}
          onRoutingFailure={(reason) => {
            if (!destroyed && token === generation) diagnostics.record(reason);
          }}
          onReady={(handle) => {
            if (destroyed || token !== generation) return;
            renderer = handle;
            if (requestedFit) {
              requestedFit = false;
              handle.fit();
            }
            if (requestedRouteFit) {
              requestedRouteFit = false;
              handle.fitRoute();
            }
            if (!readyAnnounced) {
              readyAnnounced = true;
              diagnostics.record("ready");
              ready.resolve();
              emit<ReadyDetail>("event-map:ready", {
                mapId: options.mapId,
                revision: publication.revision,
              });
              announceView(lastView ?? handle.getView());
              if (lastRoute) announceRoute(lastRoute);
            }
          }}
          onViewChange={(view) => {
            if (destroyed || token !== generation) return;
            announceView(view);
          }}
          onRouteChange={(change) => {
            if (destroyed || token !== generation) return;
            announceRoute(change);
          }}
          onSelect={(location, pin) => {
            if (destroyed || token !== generation) return;
            emit<SelectDetail>("event-map:select", {
              mapId: options.mapId,
              location,
              ...(pin ? { pin } : {}),
            });
            options.onSelect?.(location, pin);
          }}
        />
      </MapBoundary>,
    );
  }

  async function load(): Promise<void> {
    alive();
    stopRequest();
    const token = ++generation;
    diagnostics.record("load_started");
    if (token > 1) {
      ready.reject(
        new EmbedError(
          "superseded",
          "A newer map request replaced this request.",
        ),
      );
      ready = deferred();
    }
    const requestReady = ready;
    snapshot = undefined;
    renderer = undefined;
    readyAnnounced = false;
    requestedRouteFit = false;
    lastView = undefined;
    lastRoute = undefined;
    emittedView = "";
    emittedRoute = "";
    root.render(
      <div className="event-map-embed__status" role="status" aria-live="polite">
        <span className="event-map-embed__spinner" aria-hidden="true" />
        <p>Loading map…</p>
      </div>,
    );
    controller = new AbortController();
    const requestController = controller;
    let timedOut = false;
    deadline = setTimeout(() => {
      timedOut = true;
      requestController.abort();
    }, 15_000);
    try {
      const response = await fetch(url, {
        signal: requestController.signal,
        mode: "cors",
        credentials: "omit",
        redirect: "error",
        referrerPolicy: "no-referrer",
        headers: { Accept: "application/json" },
      });
      const publication = await readPublishedMap(response, options.mapId);
      if (destroyed || token !== generation) return;
      validateLocations(publication, pins, route);
      clearTimeout(deadline);
      deadline = undefined;
      controller = undefined;
      snapshot = publication;
      frame.setAttribute("aria-label", publication.map.title);
      renderMap(token);
    } catch (error) {
      showError(
        timedOut
          ? new EmbedError(
              "timeout",
              "The map took too long to load. Please try again.",
            )
          : error,
        token,
      );
    }
    return requestReady.promise;
  }

  const handle: EmbedHandle = {
    get ready() {
      return ready.promise;
    },
    getDiagnostics() {
      return diagnostics.snapshot();
    },
    setPins(nextPins: MapPin[]) {
      alive();
      const next = validatePins(nextPins);
      if (snapshot) validateLocations(snapshot, next, route);
      pins = next;
      renderMap(generation);
    },
    setRoute(nextRoute: MapRoute | null) {
      alive();
      const next = validateRoute(nextRoute);
      if (snapshot) validateLocations(snapshot, pins, next);
      route = next;
      requestedRouteFit = false;
      renderMap(generation);
    },
    clearRoute() {
      alive();
      route = null;
      requestedRouteFit = false;
      renderMap(generation);
    },
    setRouteStyle(nextStyle: MapRouteStyle) {
      alive();
      routeStyle = validateRouteStyle(nextStyle);
      renderMap(generation);
    },
    zoomIn() {
      camera().zoomIn();
    },
    zoomOut() {
      camera().zoomOut();
    },
    setZoom(value: number) {
      const current = camera();
      current.setZoom(validateControlNumber(value, "Zoom", 1, 4));
    },
    rotate(value: number) {
      const current = camera();
      current.rotate(validateControlNumber(value, "Rotation"));
    },
    setRotation(value: number) {
      const current = camera();
      current.setRotation(validateControlNumber(value, "Rotation"));
    },
    setPitch(value: number) {
      const current = camera();
      current.setPitch(validateControlNumber(value, "Pitch", 0, 70));
    },
    reset() {
      camera().reset();
    },
    clearSelection() {
      camera().clearSelection();
    },
    getView() {
      return { ...camera().getView() };
    },
    focusLocation(locationId: string) {
      const current = camera();
      if (
        typeof locationId !== "string" ||
        !snapshot?.map.locations.some((location) => location.id === locationId)
      )
        throw new EmbedError(
          "unknown_location",
          "The requested location is not on this map.",
        );
      if (isMapLocationHidden(snapshot.map, locationId))
        throw new EmbedError(
          "hidden_location",
          "The requested location is hidden on this map.",
        );
      current.focusLocation(locationId);
    },
    fitRoute() {
      camera();
      if (!route)
        throw new EmbedError("no_route", "Choose a route before fitting it.");
      // A host can setRoute() and fitRoute() together. Wait for the next render
      // so the renderer frames the new leg, including its temporary roof heights.
      requestedRouteFit = true;
      renderMap(generation);
    },
    fit() {
      alive();
      if (renderer) renderer.fit();
      else requestedFit = true;
    },
    retry() {
      return load();
    },
    destroy() {
      if (destroyed) return;
      destroyed = true;
      diagnostics.record("destroyed");
      diagnostics.close();
      generation += 1;
      stopRequest();
      ready.reject(
        new EmbedError("destroyed", "This map instance has been destroyed."),
      );
      root.unmount();
      host.remove();
      renderer = undefined;
      snapshot = undefined;
    },
  };
  void load().catch(() => {});
  return handle;
}
